import { useState } from 'react';
import type { ContractEffort, EffortBucket } from '@forecastify/shared';
import { Button, DropdownMenu, Icon, useIsMobile } from '@forecastify/ui';
import { useT } from '../../../i18n';
import { useHourFormat } from './effort-format';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C65 · TERV / TÉNY / ELŐREJELZÉS — one unit, three series, one table.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE AXIS, WHICH IS THE WHOLE DIFFERENCE FROM THE FINANCE CHART.** That one plots hours
 * against money and needs two rulers; everything here is hours, so the three series sit on one
 * scale and can be read against each other without arithmetic.
 *
 * ⚠️⚠️ **THE COLUMNS ARE PERIODS OR CALENDAR BUCKETS, AND THE WIRE DECIDES WHICH.** A retainer
 * looked at whole draws `#1 … #9`, each against its own frame — the unit the contract is written
 * in. Narrowed to one period there is only one of those, so the server buckets by day, week or
 * month instead. `budget` being non-null on a column is what says which shape arrived, and the
 * frame ticks and the `KERET` table row appear with it.
 *
 * ⚠️⚠️ **THE FORECAST IS AN OVERLAY ON THE ACTUAL, NOT A FOURTH BAR.** The mockup hatches it from
 * the baseline and draws it only where it exceeds what has been logged, so the visible hatching IS
 * the gap between where the column is and where it is going. Beside the actual it would read as a
 * separate quantity and the comparison would have to be done by eye.
 *
 * ⚠️ **Periódus / Kumulált is about the COLUMNS**, like the finance chart's — cumulative means
 * this column plus every one before it, on all three series at once.
 *
 * ⚠️ **The granularity tabs are hidden on a recurring contract**, because there the columns are
 * periods and a day/week/month choice would change nothing. The mockup's `granTabs: isRec ? []`.
 *
 * ⚠️ **CSS bars, no charting library** — the shapes are a bar and a rule.
 */

const COLOR = {
  actual: 'var(--primary)',
  plan: 'color-mix(in oklch, var(--primary) 22%, transparent)',
  frame: 'color-mix(in oklch, var(--foreground) 55%, transparent)',
  overtime: 'var(--status-warn)',
} as const;

const HATCH =
  'repeating-linear-gradient(45deg, color-mix(in oklch, var(--primary) 30%, transparent) 0 3px, transparent 3px 6px)';
const HATCH_OVER =
  'repeating-linear-gradient(45deg, color-mix(in oklch, var(--destructive) 26%, transparent) 0 3px, transparent 3px 6px)';

/**
 * ⚠️⚠️ **ONE GUTTER WIDTH, SHARED BY THE AXIS AND THE DATA TABLE'S ROW LABELS.** The table used to
 * put its `KERET / TERV / TÉNY` labels INSIDE the plot's own width, which squeezed every cell out
 * of line with the column it describes — the figures drifted further from their bars the further
 * right you read. Both now start at the same x, so a cell sits under its column by construction.
 */
const GUTTER = 'w-[96px] shrink-0';

const clamp = (value: number): number => Math.max(0, Math.min(100, value));
const pct = (value: number): string => `${value.toFixed(2)}%`;

/** The running total of a series — what Kumulált means, applied to all three at once. */
function total(values: readonly (number | null)[]): number[] {
  let sum = 0;
  return values.map((value) => (sum += value ?? 0));
}

/**
 * ⚠️⚠️ **`period` IS OURS AND THE MOCKUP HAS NO SUCH OPTION — flagged as an addition.** The mockup
 * draws `Nap / Hét / Hónap` on a retainer too and then ignores the choice: `periodMode` wins the
 * render, so all three tabs draw the same period columns and one of them sits lit for a reason the
 * reader cannot see. Rather than reproduce a dead control we made it live, which needs a fourth
 * option — the way BACK to the period columns that are the default.
 */
export type Granularity = 'period' | 'day' | 'week' | 'month';

export function EffortChart({
  view,
  granularity,
  onGranularity,
}: {
  readonly view: ContractEffort;
  readonly granularity: Granularity;
  readonly onGranularity: (granularity: Granularity) => void;
}) {
  const { hours } = useHourFormat();
  const mobile = useIsMobile();
  const t = useT();
  const [cumulative, setCumulative] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);

  const buckets = view.buckets;
  if (buckets.length === 0) {
    return (
      <section className="bg-card rounded-xl border p-4 shadow-sm">
        <p className="text-muted-foreground text-center text-[12px]">{t('effort.chart.empty')}</p>
      </section>
    );
  }

  const count = buckets.length;
  /* ⚠️ One column carrying a frame means every column does — the wire buckets by period or it
     does not, and this is the flag the ticks, the `KERET` row and the legend all read. */
  const byPeriod = buckets.some((bucket) => bucket.budget !== null);

  const planRaw = buckets.map((bucket) => Number(bucket.planned));
  const actualRaw = buckets.map((bucket) => (bucket.future ? 0 : Number(bucket.logged)));
  const frameRaw = buckets.map((bucket) => (bucket.budget === null ? null : Number(bucket.budget)));
  /**
   * ⚠️⚠️ **A CLOSED COLUMN'S FORECAST IS ITS ACTUAL, WHICH ONLY MATTERS WHEN CUMULATING.** Per
   * period a past column draws no forecast at all (`null`, a dash in the table). Running totals
   * cannot skip a term, though — a cumulative forecast that ignored every closed month would start
   * from zero halfway through the year and sit permanently below the actual line beside it.
   */
  const forecastRaw = buckets.map((bucket) =>
    bucket.projected === null ? null : Number(bucket.projected),
  );
  const forecastForTotal = buckets.map((bucket) =>
    bucket.projected === null ? Number(bucket.logged) : Number(bucket.projected),
  );

  const planBars = cumulative ? total(planRaw) : planRaw;
  const actualBars = cumulative ? total(actualRaw) : actualRaw;
  const frameBars = cumulative ? total(frameRaw) : frameRaw;
  const forecastBars = cumulative ? total(forecastForTotal) : forecastRaw;

  /* ⚠️ No 6% head-room: the bullet panel pads its scale so an overrun overhangs a fixed mark,
     but a chart's tallest column belongs at the top of its own axis. Padding it here just drew
     every column six per cent shorter than the mockup's. */
  const top = Math.max(
    1,
    ...planBars,
    ...actualBars,
    ...forecastBars.map((value) => value ?? 0),
    ...frameBars.map((value) => value ?? 0),
  );

  /**
   * ⚠️ **`solidTo` is the last column that has HAPPENED**, and three things hang off it: the wash
   * over the future, the marker, and which label is drawn in weight. `current` and `future` are
   * both on the wire, so nothing here has to guess where now is.
   */
  const firstFuture = buckets.findIndex((bucket) => bucket.future);
  const solidTo = firstFuture < 0 ? count - 1 : Math.max(0, firstFuture - 1);
  const currentIndex = buckets.findIndex((bucket) => bucket.current);
  const currentAt = currentIndex < 0 ? null : ((currentIndex + 0.5) / count) * 100;

  const plotWidth = Math.max(420, count * (byPeriod ? 54 : granularity === 'day' ? 30 : 56));
  const tip = hovered === null ? null : (buckets[hovered] ?? null);
  const tipSide =
    hovered === null
      ? {}
      : hovered > count / 2
        ? { right: pct(((count - hovered - 0.5) / count) * 100) }
        : { left: pct(((hovered + 0.5) / count) * 100) };

  const dash = (value: number | null): string => (value === null ? '–' : hours(value));

  /* ── the data table under the plot ─────────────────────────────────────────── */
  const tableRows: {
    key: string;
    label: string;
    cells: { value: string; className: string }[];
  }[] = [
    ...(byPeriod
      ? [
          {
            key: 'frame',
            label: t('effort.chart.row.frame'),
            cells: frameBars.map((value) => ({
              value: dash(value),
              className: 'text-muted-foreground',
            })),
          },
        ]
      : []),
    {
      key: 'plan',
      label: t('effort.chart.row.plan'),
      cells: planBars.map((value) => ({
        value: value > 0.004 ? hours(value) : '–',
        className: 'text-muted-foreground',
      })),
    },
    {
      key: 'actual',
      label: t('effort.chart.row.actual'),
      cells: buckets.map((bucket, position) => ({
        value: bucket.future ? '–' : hours(actualBars[position] ?? 0),
        className: bucket.future ? 'text-muted-foreground/50' : 'text-foreground font-medium',
      })),
    },
    {
      key: 'forecast',
      label: t('effort.chart.row.forecast'),
      cells: buckets.map((bucket, position) => {
        const value = forecastBars[position] ?? null;
        if (value === null) return { value: '–', className: 'text-muted-foreground/50' };
        const against = byPeriod ? (frameBars[position] ?? null) : (planBars[position] ?? null);
        const over = against !== null && against > 0.004 && value > against + 0.004;
        return {
          value: hours(value),
          className: over ? 'text-destructive' : 'text-muted-foreground',
        };
      }),
    },
  ];

  const granularities = [
    ...(view.recurring && view.selectedPeriod === null
      ? [{ value: 'period' as const, label: t('effort.chart.period') }]
      : []),
    { value: 'day' as const, label: t('effort.chart.day') },
    { value: 'week' as const, label: t('effort.chart.week') },
    { value: 'month' as const, label: t('effort.chart.month') },
  ];

  return (
    <section className="bg-card min-w-0 rounded-xl border shadow-sm">
      {/* 17 Oct 2026 · one line on a phone: the title, the legend (scrolling if it must) and the ⋯. */}
      <div
        className={`flex items-center gap-3 border-b px-4 py-3 ${mobile ? 'flex-nowrap' : 'flex-wrap'}`}
      >
        <span className="text-muted-foreground shrink-0 text-[11px] font-medium tracking-wide uppercase">
          {cumulative ? t('effort.chart.titleCumulative') : t('effort.chart.title')}
        </span>
        {mobile ? null : (
          <Switcher value={granularity} options={granularities} onChange={onGranularity} />
        )}
        <div className={mobile ? 'hidden' : 'flex-1'} />

        {/**
         * ⚠️⚠️ **THE LEGEND IS DRAWN HERE, UNLIKE THE MONEY CHART'S.** That one computes a legend
         * and renders it nowhere — one of the mockup's 140 dead keys — and its series are named by
         * the pill and the hover card instead. This chart has four series in three shapes (a solid
         * bar, a faint one behind it, a hatched overlay and a dashed rule) and nothing on the plot
         * names any of them, so the mockup draws the legend and so do we.
         */}
        <div
          className={
            mobile
              ? 'scrollbar-none flex min-w-0 flex-1 items-center justify-end gap-3 overflow-x-auto'
              : 'flex items-center gap-3'
          }
        >
          {[
            { key: 'plan', label: t('effort.chart.row.plan'), swatch: COLOR.plan, dash: false },
            {
              key: 'actual',
              label: t('effort.chart.row.actual'),
              swatch: COLOR.actual,
              dash: false,
            },
            { key: 'forecast', label: t('effort.chart.row.forecast'), swatch: HATCH, dash: false },
            ...(byPeriod
              ? [
                  {
                    key: 'frame',
                    label: t('effort.chart.row.frame'),
                    swatch: COLOR.frame,
                    dash: true,
                  },
                ]
              : []),
          ].map((entry) => (
            <span key={entry.key} className="text-muted-foreground flex items-center gap-1.5">
              <span
                aria-hidden
                className={
                  entry.dash ? 'h-0 w-3 border-t-2 border-dashed' : 'h-2.5 w-3 rounded-[2px]'
                }
                style={
                  entry.dash
                    ? { borderColor: entry.swatch }
                    : { background: entry.swatch, border: '1px solid var(--border)' }
                }
              />
              <span className="text-[10.5px]">{entry.label}</span>
            </span>
          ))}
        </div>

        {mobile ? (
          /* 17 Oct 2026 · on a phone the columns and the shape are one ⋯ — the product owner. */
          <DropdownMenu
            width={220}
            align="end"
            label={t('chart.options')}
            items={[
              ...granularities.map((option) => ({
                label: option.label,
                checked: option.value === granularity,
                onSelect: () => {
                  onGranularity(option.value);
                },
              })),
              { type: 'separator' as const, label: '' },
              {
                label: t('effort.chart.perPeriod'),
                checked: !cumulative,
                onSelect: () => {
                  setCumulative(false);
                },
              },
              {
                label: t('effort.chart.cumulative'),
                checked: cumulative,
                onSelect: () => {
                  setCumulative(true);
                },
              },
            ]}
          >
            <Button variant="ghost" size="icon-sm" aria-label={t('chart.options')}>
              <Icon name="ellipsis" size={15} />
            </Button>
          </DropdownMenu>
        ) : (
          <Switcher
            value={cumulative ? 'cumulative' : 'period'}
            options={[
              { value: 'period' as const, label: t('effort.chart.perPeriod') },
              { value: 'cumulative' as const, label: t('effort.chart.cumulative') },
            ]}
            onChange={(value) => {
              setCumulative(value === 'cumulative');
            }}
          />
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-max px-4 pt-4 pb-3">
          <div className="flex" style={{ height: 220 }}>
            <div
              className={`bg-card sticky left-0 z-20 flex flex-col justify-between pr-2 text-right ${GUTTER}`}
            >
              {[4, 3, 2, 1, 0].map((step) => (
                <span
                  key={step}
                  className="text-muted-foreground text-[10.5px] leading-none tabular-nums"
                >
                  {hours((top * step) / 4)}
                </span>
              ))}
            </div>

            <div
              className="relative min-w-0 flex-1"
              style={{ minWidth: plotWidth }}
              onMouseLeave={() => {
                setHovered(null);
              }}
            >
              {[0, 25, 50, 75, 100].map((line) => (
                <span
                  key={line}
                  aria-hidden
                  className="border-border/70 absolute inset-x-0 border-t"
                  style={{ bottom: pct(line) }}
                />
              ))}

              {/**
               * ⚠️⚠️ **WHERE *NOW* IS, WITHOUT WHICH THE CHART IS A ROW OF NUMBERS.** At the CENTRE
               * of the running column, not at the edge of the first future one: the period we are
               * inside is part spent, and a line at its left edge claims all of it is still ahead.
               */}
              {currentAt === null ? null : (
                <>
                  <span
                    aria-hidden
                    className="absolute inset-y-0 w-px"
                    style={{
                      left: pct(currentAt),
                      background: 'color-mix(in oklch, var(--foreground) 45%, transparent)',
                    }}
                  />
                  <span
                    className="bg-card text-muted-foreground absolute top-1 z-[5] rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium tracking-wide whitespace-nowrap"
                    style={{ left: pct(currentAt), transform: 'translateX(-50%)' }}
                  >
                    {t('effort.chart.current')}
                  </span>
                </>
              )}

              <div className="absolute inset-0 flex items-end gap-1">
                {buckets.map((bucket, position) => {
                  const planValue = planBars[position] ?? 0;
                  const actualValue = actualBars[position] ?? 0;
                  const forecastValue = forecastBars[position] ?? null;
                  const frameValue = frameBars[position] ?? null;
                  const showForecast =
                    forecastValue !== null && forecastValue > actualValue + 0.004;
                  const over =
                    frameValue !== null &&
                    frameValue > 0.004 &&
                    (forecastValue ?? actualValue) > frameValue + 0.004;

                  return (
                    <button
                      key={bucket.key}
                      type="button"
                      className="relative h-full min-w-0 flex-1 cursor-default"
                      aria-label={bucket.label}
                      onMouseEnter={() => {
                        setHovered(position);
                      }}
                      onFocus={() => {
                        setHovered(position);
                      }}
                      onBlur={() => {
                        setHovered(null);
                      }}
                    >
                      {bucket.future ? (
                        <span aria-hidden className="bg-muted/25 absolute inset-0" />
                      ) : null}

                      {/* ⚠️ PLAN is the wide one BEHIND; the actual the narrow one in FRONT, so
                          the overhang IS the gap between plan and reality. */}
                      <span
                        aria-hidden
                        className="absolute inset-x-[10%] bottom-0 rounded-t-[3px]"
                        style={{
                          height: pct(clamp((planValue / top) * 100)),
                          background: COLOR.plan,
                        }}
                      />
                      {showForecast ? (
                        <span
                          aria-hidden
                          className="absolute inset-x-[27%] bottom-0 rounded-t-[3px] border-t"
                          style={{
                            height: pct(clamp((forecastValue / top) * 100)),
                            background: over ? HATCH_OVER : HATCH,
                            borderColor: `color-mix(in oklch, ${
                              over ? 'var(--destructive)' : 'var(--primary)'
                            } 50%, transparent)`,
                          }}
                        />
                      ) : null}
                      <span
                        aria-hidden
                        className="absolute inset-x-[27%] bottom-0 rounded-t-[3px]"
                        style={{
                          height: pct(clamp((actualValue / top) * 100)),
                          background: COLOR.actual,
                        }}
                      />

                      {/* ⚠️ The frame is a RULE across the column, not a bar behind it — the bars
                          are quantities and this is the line they are measured against. */}
                      {frameValue === null ? null : (
                        <span
                          aria-hidden
                          className="absolute inset-x-[6%] border-t-2 border-dashed"
                          style={{
                            bottom: pct(clamp((frameValue / top) * 100)),
                            borderColor: COLOR.frame,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {tip === null || hovered === null ? null : (
                <div
                  className="bg-popover text-popover-foreground pointer-events-none absolute z-10 w-[210px] rounded-lg border p-2.5 shadow-md"
                  style={{ top: 4, ...tipSide }}
                >
                  <div className="text-[11.5px] font-medium tabular-nums">
                    {`${tip.label} · ${tip.from} – ${tip.to}`}
                  </div>
                  {frameBars[hovered] === null || frameBars[hovered] === undefined ? null : (
                    <Row
                      color={COLOR.frame}
                      label={t('effort.chart.row.frame')}
                      value={hours(frameBars[hovered])}
                    />
                  )}
                  <Row
                    color={COLOR.plan}
                    label={t('effort.chart.row.plan')}
                    value={(planBars[hovered] ?? 0) > 0.004 ? hours(planBars[hovered] ?? 0) : '–'}
                  />
                  <Row
                    color={COLOR.actual}
                    label={t('effort.chart.row.actual')}
                    value={tip.future ? '–' : hours(actualBars[hovered] ?? 0)}
                  />
                  <Row
                    color={COLOR.plan}
                    label={t('effort.chart.row.forecast')}
                    value={dash(forecastBars[hovered] ?? null)}
                    over={
                      (frameBars[hovered] ?? 0) > 0.004 &&
                      (forecastBars[hovered] ?? 0) > (frameBars[hovered] ?? 0) + 0.004
                    }
                  />
                  <Row
                    color={COLOR.overtime}
                    label={t('effort.chart.row.overtime')}
                    value={hours(Number(tip.overtime))}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-2 flex">
            <div className={`bg-card sticky left-0 z-20 ${GUTTER}`} />
            <div className="flex min-w-0 flex-1 gap-1" style={{ minWidth: plotWidth }}>
              {buckets.map((bucket, position) => (
                <span
                  key={bucket.key}
                  className={`min-w-0 flex-1 truncate text-center text-[10px] tabular-nums ${tick(bucket, position, solidTo)}`}
                >
                  {bucket.label}
                </span>
              ))}
            </div>
          </div>

          {/**
           * ⚠️⚠️ **THE TABLE IS NOT A DUPLICATE OF THE CHART, IT IS THE OTHER HALF OF IT.** A bar
           * says *bigger than the one beside it*; nobody plans against that. Every figure the
           * columns encode is readable here to the minute, in the same order, under the same
           * labels — which is also what makes the chart checkable rather than believable.
           */}
          <div className="mt-3 border-t pt-2">
            {tableRows.map((row) => (
              <div key={row.key} className="flex items-center py-[3px]">
                <span
                  className={`text-muted-foreground bg-card sticky left-0 z-20 pr-2 text-right text-[10px] font-medium tracking-wide uppercase ${GUTTER}`}
                >
                  {row.label}
                </span>
                <div className="flex min-w-0 flex-1 gap-1" style={{ minWidth: plotWidth }}>
                  {row.cells.map((cell, position) => (
                    <span
                      key={buckets[position]?.key ?? String(position)}
                      className={`min-w-0 flex-1 truncate text-center text-[10.5px] tabular-nums ${cell.className}`}
                    >
                      {cell.value}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** ⚠️ The current column is the one the reader is standing in, so it is the one drawn in weight. */
function tick(bucket: EffortBucket, position: number, solidTo: number): string {
  if (bucket.current) return 'text-foreground font-medium';
  if (position === solidTo && !bucket.future) return 'text-foreground font-medium';
  return bucket.future ? 'text-muted-foreground/60' : 'text-muted-foreground';
}

function Row({
  color,
  label,
  value,
  over = false,
}: {
  readonly color: string;
  readonly label: string;
  readonly value: string;
  readonly over?: boolean;
}) {
  return (
    <div className="mt-1 flex items-center gap-2">
      <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: color }} />
      <span className="text-muted-foreground min-w-0 flex-1 text-[10.5px] font-medium tracking-wide uppercase">
        {label}
      </span>
      <span className={`shrink-0 text-[11px] tabular-nums ${over ? 'text-destructive' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function Switcher<T extends string>({
  value,
  options,
  onChange,
}: {
  readonly value: T;
  readonly options: readonly { value: T; label: string }[];
  readonly onChange: (value: T) => void;
}) {
  return (
    <div className="bg-muted/60 flex items-center gap-0.5 rounded-md p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`h-[24px] rounded px-2 text-[11.5px] font-medium transition-colors ${
            value === option.value
              ? 'bg-background text-foreground shadow-xs'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
